import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'

import { Application, Field, FieldDataValue, PublicClassNames, Waypoints } from 'embed/constants'
import { defaultCreditScoreUrl } from 'embed/constants/config'
import { Fields } from 'embed/constants/fields'

import AdvertiserDisclosure from 'embed/components/AdvertiserDisclosure'
import { NoAffect } from 'embed/components/Application/NoAffect'
import { ReturnToSearch } from 'embed/components/Application/ReturnToSearch'
import { TCPALanguage } from 'embed/components/Application/TCPALanguage'
import { ApplicationField } from 'embed/components/ApplicationField/container'
import ApplicationSubmitButton from 'embed/components/ApplicationSubmitButton/container'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { LoanStep, Product } from 'even-ts-static'
import Waypoint from '../../../Waypoint'

const ArrowBack = require('assets/components/arrow-back.svg')
const styles = require('embed/styles/application')
const InfoIcon = require('../info-blue.svg')
const TransUnion = require('assets/components/transunion.svg')

interface Props {
  application: Application
  consent: boolean
  consentValidationStatus: boolean
  creditScoreOfferUrl: string | undefined
  directReferral: boolean
  dispatchBackToStepOne: () => void
  displayGraduateLevel: boolean
  handleConsentsToFCRAandTCPA: () => void
  onChange: (field: Field, value) => void
  onConsentChange: (event) => void
  hideLoanPurpose: boolean
  slrLoanPurposeOnChange: (value: FieldDataValue) => void
  slrEducationLevelOnChange: (value: FieldDataValue) => void
}

const BUTTON_TEXT = 'Agree and Continue'

const StudentLoanRefinanceApplicationView: React.SFC<Props> = (props) => (
  <ThemeConsumer>
    {({ backdropStyles, borderRadiusLarge, boxStyles, colorBorder, colorPrimary, fontFamilyPrimary }) => (
      <React.Fragment>
        <div
          className={classnames(
            PublicClassNames.ApplicationContainer,
            styles.container,
            css`
              background-color: ${backdropStyles.backgroundColor};
              border-color: ${colorBorder};
              border-radius: ${borderRadiusLarge};
            `,
          )}
          style={boxStyles}
        >
          {!props.directReferral && (
            <ReturnToSearch className={styles.backToSearch} onClick={props.dispatchBackToStepOne} />
          )}
          <NoAffect />
          <section className={styles.formSection}>
            <div className={styles.advertiserDisclosure}>
              <AdvertiserDisclosure />
            </div>
            <h2
              className={classnames(
                styles.heading,
                css`
                  font-family: ${fontFamilyPrimary};
                `,
              )}
            >
              Loan Info
            </h2>
            <ApplicationField product={Product.Loan} field={Fields.LoanAmount} />
            <ApplicationField
              hidden={props.hideLoanPurpose}
              onChange={props.slrLoanPurposeOnChange}
              product={Product.Loan}
              field={Fields.LoanPurpose}
            />
            <ApplicationField product={Product.Loan} field={Fields.CreditRating}>
              <div className={styles.creditScore}>
                <a
                  data-test-id="app_creditScoreLink"
                  className={css`
                    color: ${colorPrimary};
                  `}
                  href={props.creditScoreOfferUrl ?? defaultCreditScoreUrl}
                  target="_blank"
                >
                  <span>Don't know? Click here to get your score.</span>
                </a>
              </div>
            </ApplicationField>
            <Waypoint name={Waypoints.LoanInfo} />
          </section>
          <form>
            <section className={styles.formSection}>
              <h2
                className={classnames(
                  styles.heading,
                  css`
                    font-family: ${fontFamilyPrimary};
                  `,
                )}
              >
                Your Info
              </h2>
              <ApplicationField product={Product.Loan} field={Fields.EmailAddress} />
              <ApplicationField product={Product.Loan} field={Fields.DateOfBirth} />
              <ApplicationField product={Product.Loan} field={Fields.FirstName} />
              <ApplicationField product={Product.Loan} field={Fields.LastName} />
              <ApplicationField product={Product.Loan} field={Fields.PropertyStatus} />
              <ApplicationField product={Product.Loan} field={Fields.MonthlyHousingPayment} />
              <ApplicationField product={Product.Loan} field={Fields.AvailableAssets} />
              <ApplicationField
                onChange={props.slrEducationLevelOnChange}
                product={Product.Loan}
                field={Fields.SLREducation}
              />
              <ApplicationField product={Product.Loan} field={Fields.University} />
              {props.displayGraduateLevel && <ApplicationField product={Product.Loan} field={Fields.GraduateDegree} />}
              <ApplicationField product={Product.Loan} field={Fields.GraduationDate} />
              <ApplicationField product={Product.Loan} field={Fields.CitizenshipStatus} />

              <Waypoint name={Waypoints.YourInfo} />
            </section>
            <section className={styles.formSection}>
              <h2
                className={classnames(
                  styles.heading,
                  css`
                    font-family: ${fontFamilyPrimary};
                  `,
                )}
              >
                Contact Info
              </h2>
              <ApplicationField product={Product.Loan} field={Fields.AddressOne} />
              <ApplicationField product={Product.Loan} field={Fields.AddressTwo} />
              <ApplicationField product={Product.Loan} field={Fields.Zipcode} />
              <ApplicationField
                product={Product.Loan}
                field={Fields.City}
                styles={css`
                  width: calc(30% - (1rem * 2));
                `}
              />
              <ApplicationField
                product={Product.Loan}
                field={Fields.State}
                styles={css`
                  width: calc(20% - (1rem * 2));
                `}
              />
              <ApplicationField product={Product.Loan} field={Fields.PhoneNumber} />
              <Waypoint name={Waypoints.ContactInfo} />
            </section>
          </form>
          <section className={styles.formSection}>
            <h2
              className={classnames(
                styles.heading,
                css`
                  font-family: ${fontFamilyPrimary};
                `,
              )}
            >
              Employment Info
            </h2>
            <ApplicationField product={Product.Loan} field={Fields.EmploymentStatus} />
            <ApplicationField product={Product.Loan} field={Fields.AnnualIncomeLoan} />
            <ApplicationField product={Product.Loan} field={Fields.PayFrequency} />
            <ApplicationField product={Product.Loan} field={Fields.SocialSecurityNumber}>
              <div
                className={classnames(
                  styles.creditScore,
                  css`
                    color: ${colorPrimary};
                    svg path {
                      fill: ${colorPrimary};
                    }
                  `,
                )}
              >
                <InfoIcon />
                <em
                  className={css`
                    margin: auto 0px;
                  `}
                >
                  This will NOT affect your credit score
                </em>
              </div>
            </ApplicationField>
            <Waypoint name={Waypoints.EmploymentInfo} />
          </section>

          <Waypoint name={Waypoints.SubmitBtn} />
          <ApplicationSubmitButton
            product={Product.Loan}
            nextStep={LoanStep.Results}
            handleBeforeSubmit={props.handleConsentsToFCRAandTCPA}
          >
            <span className={styles.consentButtonLabel}>{BUTTON_TEXT}</span>
          </ApplicationSubmitButton>
          <TCPALanguage buttonText={BUTTON_TEXT} className={styles.consentWithButton} product={Product.Loan} />
        </div>
      </React.Fragment>
    )}
  </ThemeConsumer>
)

export default StudentLoanRefinanceApplicationView
